// Types
export interface BlockInspectorState {
  hoveredElement: HTMLElement | null;
  overlayVisible: boolean;
  overlayPosition: { top: number; left: number; width: number; height: number };
  overlayLabel: string;
}

export interface UseBlockInspectorOptions {
  containerRef: React.RefObject<HTMLElement>;
  showOverlay?: boolean;
  overlayClassName?: string;
  containerClassName?: string;
  onElementClick?: (element: HTMLElement, event: MouseEvent) => void;
  onElementHover?: (element: HTMLElement | null) => void;
  excludeSelectors?: string[];
  includeSelectors?: string[];
  showPopover?: boolean;
}

export interface InspectorConfig {
  showOverlay?: boolean;
  overlayClassName?: string;
  containerClassName?: string;
  excludeSelectors?: string[];
  includeSelectors?: string[];
  nonEditableSections?: string[];
}

// Core utility functions
export const findBlockElementAtPoint = (
  x: number,
  y: number,
  container: HTMLElement,
  config: InspectorConfig = {},
): HTMLElement | null => {
  let el = document.elementFromPoint(x, y);

  if (!el || !container.contains(el)) {
    return null;
  }

  // Check if element should be excluded
  for (const selector of config.excludeSelectors || []) {
    if ((el as HTMLElement).closest(selector)) {
      return null;
    }
  }

  // If include selectors are specified, only allow those
  if (config.includeSelectors && config.includeSelectors.length > 0) {
    const isIncluded = config.includeSelectors.some((selector) =>
      (el as HTMLElement).closest(selector),
    );

    if (!isIncluded) {
      return null;
    }
  }

  while (
    el &&
    el !== container &&
    window.getComputedStyle(el as HTMLElement).display === 'inline'
  ) {
    el = (el as HTMLElement).parentElement;
  }

  if (!el || el === container) {
    return null;
  }

  return el as HTMLElement;
};

export const findBlockLevelElements = (
  container: HTMLElement,
  config: InspectorConfig = {},
): HTMLElement[] => {
  const allElements = container.querySelectorAll('*');
  const blockElements: HTMLElement[] = [];

  for (let i = 0; i < allElements.length; i++) {
    const element = allElements[i] as HTMLElement;

    // Quick check for excluded elements first
    let shouldExclude = false;

    for (const selector of config.excludeSelectors || []) {
      if (element.closest(selector)) {
        shouldExclude = true;
        break;
      }
    }
    if (shouldExclude) {
      continue;
    }

    // Check include selectors if specified
    if (config.includeSelectors && config.includeSelectors.length > 0) {
      const isIncluded = config.includeSelectors.some((selector) =>
        element.closest(selector),
      );

      if (!isIncluded) {
        continue;
      }
    }

    // Only check display property for elements that pass the filters
    const display = window.getComputedStyle(element).display;

    if (display !== 'inline') {
      blockElements.push(element);
    }
  }

  return blockElements;
};

export const isInsideNonEditable = (
  element: HTMLElement,
  stopAtSelector: string,
  nonEditableSections: string[] = [],
): boolean => {
  let el: HTMLElement | null = element;

  while (el && !el.matches(stopAtSelector)) {
    el = el.parentElement;
  }

  if (el && el.classList) {
    for (const section of nonEditableSections) {
      if (el.classList.contains(section)) {
        return true;
      }
    }
  }

  return false;
};

export const updateOverlayPosition = (
  element: HTMLElement,
  overlayElement: HTMLElement,
): void => {
  const rect = element.getBoundingClientRect();

  overlayElement.style.top = `${rect.top - 8}px`;
  overlayElement.style.left = `${rect.left - 7}px`;
  overlayElement.style.width = `${rect.width + 10}px`;
  overlayElement.style.height = `${rect.height + 16}px`;
};

export const createOverlayElement = (
  className: string = 'inspector-overlay',
): HTMLElement => {
  const overlay = document.createElement('div');

  overlay.className = `${className} inspector-overlay`;
  document.body.appendChild(overlay);

  return overlay;
};

export const setupIntersectionObserver = (
  element: HTMLElement,
  overlayElement: HTMLElement,
  scrollContainer: HTMLElement | null,
  updatePosition: () => void,
): IntersectionObserver => {
  const observer = new IntersectionObserver(
    (entries) => {
      const entry = entries[0];

      if (entry.isIntersecting) {
        // Element is visible, show overlay
        overlayElement.classList.remove('hidden');
        overlayElement.classList.add('visible');
        // Update position immediately when element becomes visible
        updatePosition();
      } else {
        // Element is out of view, hide overlay
        overlayElement.classList.remove('visible');
        overlayElement.classList.add('hidden');
      }
    },
    {
      root: scrollContainer || null, // null = viewport
      threshold: 0.01, // Show when even 1% is visible
      rootMargin: '0px 0px -20px 0px', // Consider element "out" when bottom is 20px from viewport
    },
  );

  observer.observe(element);

  return observer;
};

export const setupScrollHandler = (
  element: HTMLElement,
  overlayElement: HTMLElement,
  scrollContainer: HTMLElement | null,
  container: HTMLElement,
  updatePosition: () => void,
): (() => void) => {
  const scrollHandler = () => {
    if (element && overlayElement) {
      updatePosition();
    }
  };

  if (scrollContainer) {
    scrollContainer.addEventListener('scroll', scrollHandler);
  } else {
    container.addEventListener('scroll', scrollHandler);
  }

  return scrollHandler;
};

export const setupResizeObserver = (
  element: HTMLElement,
  overlayElement: HTMLElement,
  container: HTMLElement,
  scrollContainer: HTMLElement | null,
  updatePosition: () => void,
): ResizeObserver => {
  const resizeObserver = new ResizeObserver(() => {
    if (element && overlayElement) {
      updatePosition();
    }
  });

  resizeObserver.observe(container);
  if (scrollContainer && scrollContainer !== container) {
    resizeObserver.observe(scrollContainer);
  }

  return resizeObserver;
};

export const cleanupInspector = (
  intersectionObserver: IntersectionObserver | null,
  mutationObserver: MutationObserver | null,
  resizeObserver: ResizeObserver | null,
  scrollHandler: (() => void) | null,
  scrollContainer: HTMLElement | null,
  container: HTMLElement | null,
  rafId: number | null,
): void => {
  if (intersectionObserver) {
    intersectionObserver.disconnect();
  }

  if (mutationObserver) {
    mutationObserver.disconnect();
  }

  if (resizeObserver) {
    resizeObserver.disconnect();
  }

  if (rafId) {
    cancelAnimationFrame(rafId);
  }

  if (scrollHandler) {
    if (scrollContainer) {
      scrollContainer.removeEventListener('scroll', scrollHandler);
    } else if (container) {
      container.removeEventListener('scroll', scrollHandler);
    }
  }
};
